import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, FileText, Calendar, DollarSign, CheckCircle2, Clock, AlertCircle, Loader2, Download, RefreshCw } from 'lucide-react';
import { invoicesService } from '../services/invoicesService';
import { downloadCSV, downloadJSON } from '../utils/exportUtils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

/**
 * Invoice Details Page
 * Displays comprehensive invoice information and approval workflow
 */
const InvoiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [approving, setApproving] = useState(false);
  const [error, setError] = useState(null);
  const [invoice, setInvoice] = useState(null);
  const [showApprovalDialog, setShowApprovalDialog] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState('');

  // Fetch invoice details
  useEffect(() => {
    if (id) {
      fetchInvoice();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchInvoice = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await invoicesService.get(parseInt(id));
      setInvoice(response.data);
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to load invoice';
      setError(errorMessage);
      console.error('Invoice fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClick = () => {
    setShowApprovalDialog(true);
    setApprovalNotes('');
  };

  const handleApproveCancel = () => {
    setShowApprovalDialog(false);
    setApprovalNotes('');
  };

  const handleApprove = async () => {
    if (approvalNotes.length > 1000) {
      alert('Approval notes must be 1000 characters or less');
      return;
    }

    try {
      setApproving(true);
      setError(null);
      
      const response = await invoicesService.approve(parseInt(id), {
        approval_notes: approvalNotes.trim() || undefined
      });
      setInvoice(response.data?.invoice || response.data);
      
      // Close dialog and show success
      setShowApprovalDialog(false);
      setApprovalNotes('');
      alert('Invoice approved successfully!');
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to approve invoice';
      setError(errorMessage);
      alert(errorMessage);
      console.error('Approve error:', err);
    } finally {
      setApproving(false);
    }
  };

  const handleRefresh = () => {
    fetchInvoice();
  };

  const handleDownload = async (format) => {
    if (!invoice) return;

    try {
      switch (format) {
        case 'csv':
          // Prepare CSV data
          const csvData = [
            {
              'Invoice Number': invoice.invoice_number,
              'Invoice ID': invoice.invoice_id,
              'Amount': invoice.amount,
              'Status': invoice.status,
              'Payment Date': invoice.payment_date || '',
              'Approved At': invoice.approved_at || '',
              'Description': invoice.description || '',
            }
          ];
          // Add app breakdown if available
          if (invoice.app_breakdown && invoice.app_breakdown.length > 0) {
            invoice.app_breakdown.forEach((app, index) => {
              csvData.push({
                'Invoice Number': '',
                'Invoice ID': '',
                'Amount': app.amount,
                'Status': `App: ${app.app_name || app.name || `App ${index + 1}`}`,
                'Payment Date': '',
                'Approved At': '',
                'Description': '',
              });
            });
          }
          downloadCSV(csvData, `invoice-${invoice.invoice_number}`);
          break;

        case 'json':
          downloadJSON(invoice, `invoice-${invoice.invoice_number}`, {
            invoice_number: invoice.invoice_number,
            exported_at: new Date().toISOString(),
          });
          break;

        case 'pdf':
          // Try backend PDF download if available
          try {
            await invoicesService.downloadPdf(parseInt(id));
          } catch (err) {
            // Fallback: show message that PDF download is not available
            alert('PDF download is not available. Please use CSV or JSON format.');
            console.error('PDF download error:', err);
          }
          break;

        default:
          break;
      }
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to download invoice';
      alert(errorMessage);
      console.error('Download error:', err);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Format datetime
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { variant: 'secondary', icon: Clock, label: 'Pending' },
      approved: { variant: 'default', icon: CheckCircle2, label: 'Approved' },
      processed: { variant: 'outline', icon: CheckCircle2, label: 'Processed' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="text-sm py-1 px-3">
        <Icon className="w-4 h-4 mr-1" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Loading invoice details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
        <div className="mt-4">
          <Button asChild variant="outline">
            <Link to="/finance/invoices">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Invoices
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <Alert>
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>Invoice not found</AlertDescription>
        </Alert>
        <div className="mt-4">
          <Button asChild variant="outline">
            <Link to="/finance/invoices">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Invoices
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  const appBreakdown = invoice.app_breakdown || [];
  const canApprove = invoice.status === 'pending';
  const workflow = invoice.workflow || {};

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      {/* Header */}
      <div className="mb-2 sm:mb-3 lg:mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm" className="text-xs">
            <Link to="/finance/invoices">
              <ArrowLeft className="w-3 h-3 mr-1.5" />
              Back
            </Link>
          </Button>
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-0.5">Invoice Details</h1>
            <p className="text-muted-foreground text-xs">{invoice.invoice_number}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRefresh}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Download
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleDownload('csv')}>
                <FileText className="w-4 h-4 mr-2" />
                Download as CSV
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDownload('json')}>
                <FileText className="w-4 h-4 mr-2" />
                Download as JSON
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDownload('pdf')}>
                <FileText className="w-4 h-4 mr-2" />
                Download as PDF
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {canApprove && (
            <Button
              onClick={handleApproveClick}
              disabled={approving}
              size="sm"
            >
              <CheckCircle2 className="w-4 h-4 mr-2" />
              Approve Invoice
            </Button>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <Alert variant="destructive" className="mb-2 sm:mb-3 lg:mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Invoice Information */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-2 sm:gap-3 lg:gap-4 mb-2 sm:mb-3 lg:mb-4">
        {/* Main Invoice Card */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Invoice Information</span>
              {getStatusBadge(invoice.status)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Invoice Number</label>
                  <p className="text-lg font-semibold">{invoice.invoice_number}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Invoice ID</label>
                  <p className="text-lg font-mono text-sm break-all">{invoice.invoice_id}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    Amount
                  </label>
                  <p className="text-xl sm:text-2xl font-bold">{formatCurrency(invoice.amount)}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                    <Calendar className="w-4 h-4" />
                    Payment Date
                  </label>
                  <p className="text-base sm:text-lg">{formatDate(invoice.payment_date)}</p>
                </div>
              </div>

              {invoice.description && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Description</label>
                  <p className="text-sm mt-1">{invoice.description}</p>
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4 border-t">
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Created At</label>
                  <p className="text-sm">{formatDateTime(invoice.created_at)}</p>
                </div>
                {invoice.approved_at && (
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Approved At</label>
                    <p className="text-sm">{formatDateTime(invoice.approved_at)}</p>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Workflow Card */}
        <Card>
          <CardHeader>
            <CardTitle>Workflow</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-muted-foreground">Status</label>
                <div className="mt-1">
                  {getStatusBadge(invoice.status)}
                </div>
              </div>

              {workflow.action && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Last Action</label>
                  <p className="text-sm capitalize">{workflow.action}</p>
                </div>
              )}

              {workflow.approved_by && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Approved By</label>
                  <p className="text-sm">User ID: {workflow.approved_by}</p>
                </div>
              )}

              {workflow.approval_notes && (
                <div>
                  <label className="text-sm font-medium text-muted-foreground">Approval Notes</label>
                  <p className="text-sm mt-1 p-2 bg-muted rounded">{workflow.approval_notes}</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* App Breakdown */}
      {appBreakdown.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>App Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>App Name</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Percentage</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {appBreakdown.map((app, index) => {
                  const percentage = invoice.amount > 0 
                    ? ((app.amount / invoice.amount) * 100).toFixed(2) 
                    : '0.00';
                  
                  return (
                    <TableRow key={index}>
                      <TableCell className="font-medium">
                        {app.app_name || app.name || `App ${index + 1}`}
                      </TableCell>
                      <TableCell>{formatCurrency(app.amount)}</TableCell>
                      <TableCell>{percentage}%</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Empty App Breakdown */}
      {appBreakdown.length === 0 && (
        <Card>
          <CardHeader>
            <CardTitle>App Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No app breakdown available for this invoice</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Approval Dialog */}
      {showApprovalDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md mx-4">
            <CardHeader>
              <CardTitle>Approve Invoice</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-4">
                  Are you sure you want to approve invoice <strong>{invoice.invoice_number}</strong> for{' '}
                  <strong>{formatCurrency(invoice.amount)}</strong>?
                </p>
              </div>
              <div>
                <Label htmlFor="approval-notes">Approval Notes (Optional)</Label>
                <textarea
                  id="approval-notes"
                  className="w-full mt-1.5 min-h-[100px] px-3 py-2 text-sm border rounded-md resize-none"
                  placeholder="Add any notes or reason for approval..."
                  value={approvalNotes}
                  onChange={(e) => setApprovalNotes(e.target.value)}
                  maxLength={1000}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {approvalNotes.length}/1000 characters
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button
                  variant="outline"
                  onClick={handleApproveCancel}
                  disabled={approving}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleApprove}
                  disabled={approving}
                >
                  {approving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                      Approve
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default InvoiceDetails;
